import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function ManagerMessages() {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [staff, setStaff] = useState([]);
  const [activeUserId, setActiveUserId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [newRecipient, setNewRecipient] = useState("");

  const loadThreads = () =>
    api.get("/messages/threads").then((r) => setThreads(r.data));

  useEffect(() => {
    loadThreads();
    api.get("/users/my-staff").then((r) => setStaff(r.data));
  }, []);

  useEffect(() => {
    if (!activeUserId) return;
    api.get(`/messages?with_user_id=${activeUserId}`).then((r) => {
      setMessages(r.data.reverse());
      r.data.filter((m) => m.to_user_id === user.id && m.status === "unread").forEach((m) =>
        api.post(`/messages/${m.id}/read`).catch(() => {})
      );
    });
  }, [activeUserId, user]);

  const send = async () => {
    if (!reply.trim()) return;
    try {
      await api.post("/messages", { to_user_id: activeUserId, body: reply, message_type: "normal" });
      setReply("");
      const { data } = await api.get(`/messages?with_user_id=${activeUserId}`);
      setMessages(data.reverse());
      loadThreads();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (activeUserId) {
    const partner = staff.find((s) => s.id === activeUserId) || threads.find((t) => t.with_user_id === activeUserId)?.with_user;
    return (
      <div className="space-y-3 max-w-3xl" data-testid="manager-chat">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setActiveUserId(null)}><ArrowLeft className="w-4 h-4" /></Button>
          <h1 className="text-xl font-bold text-slate-900">{partner?.name || "メッセージ"}</h1>
        </div>
        <Card className="p-4 bg-white border border-slate-200 space-y-2 max-h-[600px] overflow-y-auto">
          {messages.map((m) => {
            const mine = m.from_user_id === user.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[70%] rounded-lg p-3 text-sm ${
                  mine ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-900"
                }`}>
                  {m.message_type === "work_request" && (
                    <p className={`text-xs font-semibold mb-1 ${mine ? "text-orange-200" : "text-orange-600"}`}>出勤依頼</p>
                  )}
                  {m.related_date && <p className="text-xs opacity-80 mb-1">対象: {m.related_date}</p>}
                  <p className="whitespace-pre-wrap">{m.body}</p>
                  {m.actions?.length > 0 && (
                    <p className="text-xs italic mt-1 opacity-80">返信: {m.actions[0].action}</p>
                  )}
                  <p className="text-[10px] opacity-60 mt-1">{m.created_at?.slice(0,16).replace("T", " ")}</p>
                </div>
              </div>
            );
          })}
        </Card>
        <div className="flex gap-2">
          <Input data-testid="manager-msg-input" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="メッセージを入力" />
          <Button data-testid="manager-msg-send" onClick={send} className="bg-blue-600 hover:bg-blue-700">送信</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl" data-testid="manager-messages-page">
      <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">メッセージ</h1>

      <Card className="p-4 bg-white border border-slate-200">
        <h2 className="text-sm font-bold text-slate-900 mb-2">新規メッセージ</h2>
        <div className="flex gap-2">
          <Select value={newRecipient} onValueChange={setNewRecipient}>
            <SelectTrigger data-testid="new-recipient" className="w-64"><SelectValue placeholder="スタッフを選択" /></SelectTrigger>
            <SelectContent>
              {staff.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button data-testid="open-new-chat" disabled={!newRecipient} onClick={() => setActiveUserId(newRecipient)} className="bg-blue-600 hover:bg-blue-700">
            会話を開く
          </Button>
        </div>
      </Card>

      <div className="space-y-2">
        {threads.map((t) => (
          <button
            key={t.with_user_id}
            data-testid={`mgr-thread-${t.with_user_id}`}
            onClick={() => setActiveUserId(t.with_user_id)}
            className="w-full text-left bg-white border border-slate-200 rounded-md p-3 flex items-center justify-between hover:border-blue-300"
          >
            <div>
              <p className="font-semibold text-slate-900">{t.with_user?.name}</p>
              <p className="text-xs text-slate-500 truncate max-w-[400px]">{t.last_message?.body}</p>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs rounded-full px-2 py-0.5 border ${
                t.last_message?.status === "unread" ? "bg-red-50 text-red-700 border-red-200" :
                t.last_message?.status === "accepted" ? "bg-green-50 text-green-700 border-green-200" :
                t.last_message?.status === "rejected" ? "bg-slate-100 text-slate-600 border-slate-200" :
                t.last_message?.status === "counter_offered" ? "bg-yellow-50 text-yellow-700 border-yellow-200" :
                t.last_message?.status === "awaiting_reply" ? "bg-yellow-50 text-yellow-700 border-yellow-200" :
                "bg-slate-100 text-slate-600 border-slate-200"
              }`}>{t.last_message?.status}</span>
              {t.unread > 0 && (
                <span className="bg-red-600 text-white text-xs font-bold rounded-full px-2 py-0.5">{t.unread}</span>
              )}
            </div>
          </button>
        ))}
        {threads.length === 0 && <p className="text-sm text-slate-500">まだメッセージがありません</p>}
      </div>
    </div>
  );
}
